import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { UploadCloud } from "lucide-react";
import { api, formatApiError, API_BASE } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useBranding, resolveLogo } from "@/contexts/BrandingContext";

// Hard-coded identity palette (LOCKED — STAS cannot change colours).
const DEFAULTS_FALLBACK = { theme: "#0a0a0a", accent: "#d4af37" };

/**
 * STAS-only Branding & Identity page.
 *  - Drag & Drop logo upload
 *  - Edit Arabic/English names + tagline + footer
 *  - Pick theme colors
 *  - Live preview of how the header & portal will render
 */
export default function BrandingPage() {
  const { user } = useAuth();
  const { lang, dir, theme } = useI18n();
  const { branding, refresh } = useBranding();
  const navigate = useNavigate();
  const isRoot = !!user?.role?.is_root;

  const [form, setForm] = useState(branding);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setForm(branding); }, [branding]);

  const T = lang === "ar"
    ? {
        title: "الهوية والعلامة التجارية",
        back: "رجوع",
        section_logo: "الشعار الرسمي",
        drop_here: "اسحب صورة هنا، أو انقر للاختيار",
        formats: "PNG / JPG / WEBP / SVG — حتى 5 ميجا",
        choose: "اختيار ملف",
        reset_logo: "إعادة للشعار الافتراضي",
        section_names: "أسماء التطبيق",
        section_colors: "ألوان الواجهة",
        section_footer: "نص الفوتر",
        section_identity: "بيانات الترخيص والاتصال",
        identity_hint: "تظهر في فوتر البوابة وفوتر النظام الداخلي لجميع المستخدمين.",
        tourism_license_number: "رقم تصريح السياحة",
        commercial_register: "السجل التجاري",
        contact_phone: "هاتف المكتب",
        contact_email: "البريد الإلكتروني",
        whatsapp_number: "واتساب خدمة العملاء",
        location_url: "رابط الموقع على خرائط جوجل",
        app_name_ar: "الاسم بالعربية",
        app_name_en: "الاسم بالإنجليزية",
        short_name: "الاسم القصير (للأيقونة)",
        tagline_ar: "العبارة التعريفية (عربي)",
        tagline_en: "العبارة التعريفية (إنجليزي)",
        theme_color: "لون الواجهة",
        background_color: "لون الخلفية",
        accent_color: "لون التمييز",
        footer_text_ar: "نص الحقوق (عربي)",
        footer_text_en: "نص الحقوق (إنجليزي)",
        footer_hint: "استخدم {year} لإدراج السنة الحالية تلقائياً.",
        save: "حفظ التغييرات",
        saving: "جاري الحفظ…",
        saved: "تم الحفظ بنجاح",
        preview: "معاينة فورية",
        preview_header: "البار العلوي",
        preview_footer: "الفوتر",
        not_root: "هذه الصفحة متاحة لـ STAS فقط.",
        bump: "ملاحظة: بعد التحديث، الأيقونة المثبتة على الجهاز قد تأخذ حتى دقيقة للتحديث (PWA cache).",
      }
    : {
        title: "Branding & Identity",
        back: "Back",
        section_logo: "Official logo",
        drop_here: "Drag an image here, or click to pick",
        formats: "PNG / JPG / WEBP / SVG — up to 5 MB",
        choose: "Choose file",
        reset_logo: "Reset to default logo",
        section_names: "App names",
        section_colors: "Theme colors",
        section_footer: "Footer text",
        section_identity: "License & contact details",
        identity_hint: "Appears in the portal footer and the internal app footer for everyone.",
        tourism_license_number: "Tourism license number",
        commercial_register: "Commercial register",
        contact_phone: "Office phone",
        contact_email: "Email address",
        whatsapp_number: "Customer-service WhatsApp",
        location_url: "Google Maps location URL",
        app_name_ar: "Arabic name",
        app_name_en: "English name",
        short_name: "Short name (for icon)",
        tagline_ar: "Tagline (Arabic)",
        tagline_en: "Tagline (English)",
        theme_color: "Theme color",
        background_color: "Background color",
        accent_color: "Accent color",
        footer_text_ar: "Footer text (AR)",
        footer_text_en: "Footer text (EN)",
        footer_hint: "Use {year} to insert the current year automatically.",
        save: "Save changes",
        saving: "Saving…",
        saved: "Saved",
        preview: "Live preview",
        preview_header: "Top bar",
        preview_footer: "Footer",
        not_root: "STAS only.",
        bump: "Note: After updating, the installed icon may take up to a minute to refresh (PWA cache).",
      };

  if (!isRoot) {
    return (
      <div dir={dir} style={{ padding: "40px", textAlign: "center", color: theme.errorText }}>
        {T.not_root}
      </div>
    );
  }

  const onPick = (e) => uploadFile(e.target.files?.[0]);

  const uploadFile = async (file) => {
    if (!file) return;
    setErr(""); setMsg(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("vta_token");
      await axios.post(`${API_BASE}/admin/branding/logo`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await refresh();
      setMsg(T.saved);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setUploading(false); }
  };

  const resetLogo = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.delete("/admin/branding/logo");
      await refresh();
      setMsg(T.saved);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const patch = {
        app_name_ar: form.app_name_ar,
        app_name_en: form.app_name_en,
        short_name: form.short_name,
        tagline_ar: form.tagline_ar,
        tagline_en: form.tagline_en,
        // Colours are LOCKED to the identity palette — always send the
        // canonical values to overwrite any earlier overrides in DB.
        theme_color: DEFAULTS_FALLBACK.theme,
        background_color: DEFAULTS_FALLBACK.theme,
        accent_color: DEFAULTS_FALLBACK.accent,
        footer_text_ar: form.footer_text_ar,
        footer_text_en: form.footer_text_en,
        // Identity credentials + contact channels — sent on every save so
        // STAS can clear or update any field at will.
        tourism_license_number: form.tourism_license_number || "",
        commercial_register: form.commercial_register || "",
        contact_phone: form.contact_phone || "",
        contact_email: form.contact_email || "",
        whatsapp_number: form.whatsapp_number || "",
        location_url: form.location_url || "",
      };
      await api.patch("/admin/branding", patch);
      await refresh();
      setMsg(T.saved);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const onChange = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) uploadFile(f);
  };

  const css = makeStyles(theme);
  const previewName = lang === "ar" ? form.app_name_ar : form.app_name_en;
  const previewFooter = (lang === "ar" ? form.footer_text_ar : form.footer_text_en)
    ?.replace("{year}", String(new Date().getFullYear()));

  return (
    <div dir={dir} style={css.page} data-testid="branding-page">
      <header style={css.header}>
        <button onClick={() => navigate("/")} style={css.backBtn} data-testid="branding-back">
          {dir === "rtl" ? "→" : "←"} {T.back}
        </button>
        <div style={css.title}>{T.title}</div>
        <button onClick={save} style={css.primaryBtn} disabled={busy} data-testid="branding-save">
          {busy ? T.saving : T.save}
        </button>
      </header>

      {(err || msg) && (
        <div style={err ? css.error : css.success} data-testid={err ? "branding-error" : "branding-success"}>
          {err || msg}
        </div>
      )}

      {/* ───── Logo ───── */}
      <section style={css.card} data-testid="section-logo">
        <h3 style={css.sectionTitle}>{T.section_logo}</h3>
        <div style={css.logoRow}>
          <img
            src={resolveLogo(form.logo_url)}
            alt="logo"
            style={css.logoPreview}
            data-testid="branding-logo-preview"
          />
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            style={css.drop(dragOver, uploading)}
            data-testid="branding-dropzone"
          >
            <div style={css.dropIcon}><UploadCloud size={36} strokeWidth={1.8} /></div>
            <div style={css.dropText}>{uploading ? "…" : T.drop_here}</div>
            <div style={css.dropFormats}>{T.formats}</div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              style={{ display: "none" }}
              onChange={onPick}
              data-testid="branding-file-input"
            />
          </div>
        </div>
        <div style={css.btnRow}>
          <button onClick={() => fileRef.current?.click()} style={css.ghostBtn} data-testid="branding-choose-file">
            {T.choose}
          </button>
          <button onClick={resetLogo} style={css.ghostBtn} disabled={busy} data-testid="branding-reset-logo">
            {T.reset_logo}
          </button>
        </div>
        <div style={css.hint}>{T.bump}</div>
      </section>

      {/* ───── Names ───── */}
      <section style={css.card} data-testid="section-names">
        <h3 style={css.sectionTitle}>{T.section_names}</h3>
        <Field label={T.app_name_ar}>
          <input style={css.input} value={form.app_name_ar || ""} onChange={onChange("app_name_ar")} dir="rtl" data-testid="input-app-name-ar" />
        </Field>
        <Field label={T.app_name_en}>
          <input style={css.input} value={form.app_name_en || ""} onChange={onChange("app_name_en")} dir="ltr" data-testid="input-app-name-en" />
        </Field>
        <Field label={T.short_name}>
          <input style={css.input} value={form.short_name || ""} onChange={onChange("short_name")} maxLength={20} data-testid="input-short-name" />
        </Field>
        <Field label={T.tagline_ar}>
          <input style={css.input} value={form.tagline_ar || ""} onChange={onChange("tagline_ar")} dir="rtl" data-testid="input-tagline-ar" />
        </Field>
        <Field label={T.tagline_en}>
          <input style={css.input} value={form.tagline_en || ""} onChange={onChange("tagline_en")} dir="ltr" data-testid="input-tagline-en" />
        </Field>
      </section>

      {/* ───── Colors (LOCKED — hard-coded brand identity) ───── */}
      <section style={css.card} data-testid="section-colors">
        <h3 style={css.sectionTitle}>{T.section_colors}</h3>
        <div style={{
          padding: 14, borderRadius: 12,
          background: theme.surface,
          border: `1px dashed ${theme.borderSoft}`,
          color: theme.textMuted, fontSize: 13, lineHeight: 1.7,
        }} data-testid="colors-locked-notice">
          <strong style={{ color: theme.gold }}>الألوان مُقفلة على الهوية الرسمية:</strong>
          {" "}الخلفية الداكنة ({DEFAULTS_FALLBACK.theme}) + الذهبي ({DEFAULTS_FALLBACK.accent}) — لا يمكن تعديلها لضمان ثبات الهوية البصرية على جميع الشاشات.
        </div>
      </section>

      {/* ───── Identity & contact (license + CR + phone + email + WhatsApp) ───── */}
      <section style={css.card} data-testid="section-identity">
        <h3 style={css.sectionTitle}>{T.section_identity}</h3>
        <div style={css.hint}>{T.identity_hint}</div>
        <Field label={T.tourism_license_number}>
          <input
            style={css.input}
            value={form.tourism_license_number || ""}
            onChange={onChange("tourism_license_number")}
            dir="ltr"
            placeholder="76/12345"
            data-testid="input-tourism-license"
          />
        </Field>
        <Field label={T.commercial_register}>
          <input
            style={css.input}
            value={form.commercial_register || ""}
            onChange={onChange("commercial_register")}
            dir="ltr"
            placeholder="1010XXXXXX"
            data-testid="input-cr"
          />
        </Field>
        <Field label={T.contact_phone}>
          <input
            style={css.input}
            value={form.contact_phone || ""}
            onChange={onChange("contact_phone")}
            dir="ltr"
            placeholder="+966 11 000 0000"
            data-testid="input-contact-phone"
          />
        </Field>
        <Field label={T.contact_email}>
          <input
            style={css.input}
            type="email"
            value={form.contact_email || ""}
            onChange={onChange("contact_email")}
            dir="ltr"
            placeholder="info@visatravelsa.com"
            data-testid="input-contact-email"
          />
        </Field>
        <Field label={T.whatsapp_number}>
          <input
            style={css.input}
            value={form.whatsapp_number || ""}
            onChange={onChange("whatsapp_number")}
            dir="ltr"
            placeholder="+966 5X XXX XXXX"
            data-testid="input-whatsapp"
          />
        </Field>
        <Field label={T.location_url}>
          <input
            style={css.input}
            value={form.location_url || ""}
            onChange={onChange("location_url")}
            dir="ltr"
            placeholder="https://maps.app.goo.gl/..."
            data-testid="input-location-url"
          />
        </Field>
      </section>

      {/* ───── Footer ───── */}
      <section style={css.card} data-testid="section-footer">
        <h3 style={css.sectionTitle}>{T.section_footer}</h3>
        <Field label={T.footer_text_ar}>
          <input style={css.input} value={form.footer_text_ar || ""} onChange={onChange("footer_text_ar")} dir="rtl" data-testid="input-footer-ar" />
        </Field>
        <Field label={T.footer_text_en}>
          <input style={css.input} value={form.footer_text_en || ""} onChange={onChange("footer_text_en")} dir="ltr" data-testid="input-footer-en" />
        </Field>
        <div style={css.hint}>{T.footer_hint}</div>
      </section>

      {/* ───── Preview ───── */}
      <section style={css.card} data-testid="section-preview">
        <h3 style={css.sectionTitle}>{T.preview}</h3>
        <div style={css.previewLabel}>{T.preview_header}</div>
        <div style={css.previewHeader}>
          <img src={resolveLogo(form.logo_url)} alt="" style={{ width: 24, height: 24, marginInlineEnd: 8 }} />
          <span style={{ fontWeight: 800, color: form.accent_color || theme.gold }}>{previewName}</span>
        </div>
        <div style={css.previewLabel}>{T.preview_footer}</div>
        <div style={css.previewFooter}>{previewFooter}</div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: "14px" }}>
      <div style={{ fontSize: "11px", marginBottom: "4px", opacity: 0.7, letterSpacing: "0.4px" }}>{label}</div>
      {children}
    </label>
  );
}

function ColorPicker({ label, value, onChange, testId }) {
  return (
    <label style={{ flex: "1 1 140px" }}>
      <div style={{ fontSize: "11px", marginBottom: "6px", opacity: 0.7 }}>{label}</div>
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 42, height: 36, padding: 0, border: "none", background: "transparent", cursor: "pointer" }}
          data-testid={`${testId}-picker`}
        />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          style={{
            flex: 1, padding: "8px 10px", borderRadius: 8,
            border: "1px solid #444", background: "transparent",
            color: "inherit", fontFamily: "monospace", fontSize: 12,
          }}
          data-testid={`${testId}-hex`}
        />
      </div>
    </label>
  );
}

const makeStyles = (theme) => ({
  page: {
    minHeight: "100vh",
    background: theme.bg,
    color: theme.text,
    fontFamily: "'Tajawal','Segoe UI', sans-serif",
    padding: "16px",
    maxWidth: "900px",
    margin: "0 auto",
  },
  header: {
    display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center",
    justifyContent: "space-between", marginBottom: "16px", padding: "8px 0",
  },
  backBtn: {
    background: "transparent", border: `1px solid ${theme.border}`,
    color: theme.text, borderRadius: "8px", padding: "8px 12px",
    cursor: "pointer", fontSize: "13px",
  },
  title: { fontSize: "18px", fontWeight: 800, color: theme.gold, flex: 1, textAlign: "center", minWidth: 140 },
  primaryBtn: {
    background: theme.goldGradient, color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none", padding: "9px 18px", borderRadius: "8px",
    fontWeight: 800, cursor: "pointer", fontSize: "13px",
  },
  card: {
    background: theme.surfaceCard,
    border: `1px solid ${theme.borderSoft}`,
    borderRadius: "14px",
    padding: "18px",
    marginBottom: "16px",
  },
  sectionTitle: { margin: "0 0 14px", fontSize: "14px", fontWeight: 800, color: theme.gold },
  logoRow: { display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" },
  logoPreview: {
    width: "96px", height: "96px", objectFit: "contain",
    background: "transparent", border: `1px dashed ${theme.borderSoft}`,
    borderRadius: "12px", padding: "8px",
  },
  drop: (active, uploading) => ({
    flex: "1 1 220px",
    minHeight: "120px",
    border: `2px dashed ${active ? theme.gold : theme.border}`,
    borderRadius: "12px",
    background: active ? (theme.name === "dark" ? "rgba(212,175,55,0.06)" : "rgba(184,144,42,0.08)") : "transparent",
    display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
    cursor: uploading ? "wait" : "pointer",
    opacity: uploading ? 0.55 : 1,
    transition: "all 150ms ease",
    gap: "6px",
    padding: "16px",
    textAlign: "center",
  }),
  dropIcon: { fontSize: "22px", color: theme.gold },
  dropText: { fontSize: "13px", fontWeight: 700, color: theme.text },
  dropFormats: { fontSize: "11px", color: theme.textMuted },
  btnRow: { display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "12px" },
  ghostBtn: {
    background: "transparent", border: `1px solid ${theme.border}`,
    color: theme.text, padding: "7px 14px", borderRadius: "8px",
    cursor: "pointer", fontWeight: 700, fontSize: "12px",
  },
  hint: { fontSize: "11px", color: theme.textMuted, marginTop: "10px", lineHeight: 1.6 },
  input: {
    width: "100%", background: theme.inputBg,
    border: `1px solid ${theme.inputBorder}`, color: theme.text,
    padding: "9px 11px", borderRadius: "8px", fontSize: "13px",
    fontFamily: "inherit", boxSizing: "border-box",
  },
  colorRow: { display: "flex", gap: "12px", flexWrap: "wrap" },
  previewLabel: { fontSize: "10px", color: theme.textMuted, marginBottom: "6px", marginTop: "8px", letterSpacing: "0.4px" },
  previewHeader: {
    display: "flex", alignItems: "center", padding: "10px 12px",
    background: theme.headerBg, border: `1px solid ${theme.borderSoft}`,
    borderRadius: "8px",
  },
  previewFooter: {
    padding: "10px", textAlign: "center", color: theme.textMuted,
    background: theme.headerBg, border: `1px solid ${theme.borderSoft}`,
    borderRadius: "8px", fontSize: "11px",
  },
  error: {
    padding: "10px 12px", background: theme.errorBg, border: `1px solid ${theme.errorBorder}`,
    color: theme.errorText, borderRadius: "8px", marginBottom: "12px", fontSize: "13px",
  },
  success: {
    padding: "10px 12px", background: "rgba(34,197,94,0.12)",
    border: "1px solid rgba(34,197,94,0.45)", color: "#16a34a",
    borderRadius: "8px", marginBottom: "12px", fontSize: "13px",
  },
});
